import { usePlayControllerHeroV1, type HeroResponse } from '@kidshome/api-contract';
import { useFeature } from '@/entities/session';
import { Feature } from '@/shared/lib/features';
import { useEquippedLook, type EquippedLook } from '@/entities/cosmetics';
import { heroLevel, formIndexForLevel, type HeroLevel } from '@/entities/hero/hero-level';
import { EFFORT_RATES_FALLBACK, type EffortRates } from '@/entities/hero/effort-bond';

/**
 * THE current hero's STATE (ADR-0102 consolidation) — the ONE place the app learns "who is the kid's hero,
 * what form is she, what is she wearing, how strong is the bond". It folds the two backend reads every hero
 * surface used to wire up by hand — the hero (`GET /v1/play/hero`) + the equipped look — plus the per-hero
 * ✨ LEVEL (ADR-0103, server-resolved), into a single {@link HeroState}. Every surface (the `/hero` home, the wardrobe, the Home
 * presence strip, the narrator bubbles, the learning/chore/profile thumbnails) calls THIS and then just
 * renders what it needs (avatar · stage · presence card · feed face) — no surface re-fetches the hero or
 * re-derives its display state, so they can never disagree about the same hero.
 *
 * Gated on `Feature.Play` (off → `hero: null`, never fetches); pass `enabled: false` to additionally gate on
 * a caller condition (e.g. the wardrobe's `cosmetic:purchase`). TanStack Query dedupes the underlying reads,
 * so calling `useHero()` from many components in one render is one request.
 *
 * `childId` is the guardian path (mirrors {@link useEquippedLook}): both underlying reads accept `?childId=`
 * so a guardian surface (`/family/:childId/hero`) resolves a CHILD's hero + look — the same unified state,
 * scoped to the same child, so the figure and its worn cosmetics can never mix children. Omit for self.
 */
export interface HeroState {
  /** The chosen archetype id (e.g. `princess`). For a degraded hero this is still its id; see `degraded`. */
  readonly archetypeId: string;
  readonly stageIndex: number;
  readonly stageCount: number;
  /** The equipped cosmetics (place → item id), empty when nothing is worn — drives every worn render. */
  readonly look: EquippedLook;
  /** The per-hero ✨ LEVEL standing (ADR-0103, server-resolved + ring fraction) — the one source for level UI. */
  readonly level: HeroLevel;
  /** The kid-given name (null until named). */
  readonly name: string | null;
  /** The archetype display-name i18n key (resolved via the play namespace). */
  readonly nameKey: string;
  readonly stage: HeroResponse['stage'];
  readonly nextStage: HeroResponse['nextStage'];
  /** A retired/legacy/unresolved hero → render a calm neutral presence (no form/next-form scene). */
  readonly degraded: boolean;
  /** The raw server response, for the few surfaces that need `nextStage.progress` etc. */
  readonly raw: HeroResponse;
}

export interface UseHeroResult {
  readonly hero: HeroState | null;
  /**
   * The SERVER-owned per-effort award table (ADR-0095 §2.1 · ADR-0101 §2) — how much ✨/bond one
   * chore/habit/learning effort earns. Every "+N" surface renders THESE amounts. Present even before a
   * hero is chosen; falls back to the local mirror only while the read is unresolved (offline/first paint).
   */
  readonly effortRates: EffortRates;
  readonly isPending: boolean;
  readonly isError: boolean;
  readonly refetch: () => void;
}

/** Resolve the current hero's unified state. See {@link HeroState}. */
export function useHero(opts?: { enabled?: boolean; childId?: string | null }): UseHeroResult {
  const hasFeature = useFeature();
  const enabled = hasFeature(Feature.Play) && (opts?.enabled ?? true);
  const childId = opts?.childId ?? null;

  const query = usePlayControllerHeroV1(childId ? { childId } : undefined, { query: { enabled } });
  // The viewed child's worn cosmetics — the SAME look every worn render uses, so a hat put on in the
  // wardrobe shows up on the hero page, the Home strip, and every avatar at once. Memoized for a stable
  // reference (so the heavy figures don't re-render on unrelated polls).
  const look = useEquippedLook({ enabled, childId });

  const data = query.data?.hero ?? null;
  const lvl = data ? heroLevel(data.level) : null;
  const hero: HeroState | null =
    data && lvl
      ? {
          archetypeId: data.archetypeId,
          // The rendered FORM follows the per-hero ✨ LEVEL (ADR-0103) — forms are milestone labels on the
          // hero's OWN level ladder — NOT the server's legacy `stageIndex` (a bond FSM over the kid-GLOBAL
          // bond, which makes a freshly-unlocked hero on a high-bond kid jump ahead a form). So a level-1
          // hero shows its first form everywhere, consistent with the "Level N · {form}" read-out.
          stageIndex: formIndexForLevel(data.archetypeId, lvl.value, data.stageCount),
          stageCount: data.stageCount,
          look,
          level: lvl,
          name: data.name ?? null,
          nameKey: data.nameKey,
          stage: data.stage,
          nextStage: data.nextStage,
          degraded: data.degraded,
          raw: data,
        }
      : null;

  return {
    hero,
    effortRates: query.data?.effortRates ?? EFFORT_RATES_FALLBACK,
    isPending: query.isPending,
    isError: query.isError,
    refetch: () => void query.refetch(),
  };
}
